import { useState } from "react";
import { formatShortDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  ArrowBendUpRight,
  CalendarDots,
  CheckCircle,
  ClockCountdown,
  Eye,
  FileText,
  LinkBreak,
  ArrowsMerge,
  Sparkle,
  WarningCircle,
} from "@phosphor-icons/react";
import { obligationActionLabel } from "@/lib/obligationsApi";

const ownerStyles = {
  A: "border-[hsl(var(--input))] bg-[hsl(var(--background))] text-[hsl(var(--muted-foreground))]",
  B: "border-[hsl(var(--input))] bg-[hsl(var(--background))] text-[hsl(var(--muted-foreground))]",
  joint: "border-[hsl(var(--accent))] bg-[hsl(var(--background))] text-[#8a6d3f]",
};

const statusStyles = {
  positive: "bg-[#e7efe6] text-[#4f7a5b]",
  attention: "bg-[#f5ecd9] text-[#9a6f2b]",
  late: "bg-[#f3e2de] text-[#a4483d]",
  neutral: "bg-[#ecebe6] text-[#6b7384]",
};

const stateIcons = {
  attention: WarningCircle,
  positive: CheckCircle,
  neutral: ClockCountdown,
  late: WarningCircle,
  check: CheckCircle,
  gate: ClockCountdown,
  void: LinkBreak,
  merge: ArrowsMerge,
};

const lanePillLabel = {
  needs_you: "Needs you",
  waiting_on_them: "Waiting on them",
  gated: "Gated",
  handled: "Handled",
  voided: "Voided",
  merged: "Merged",
};

const channelIcons = {
  capture: Sparkle,
  feeder: Sparkle,
  note: Sparkle,
  text: ArrowBendUpRight,
  sms: ArrowBendUpRight,
  email: FileText,
  bank: CheckCircle,
};

const formatMoney = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const number = Number(value);
  if (Number.isNaN(number)) return value;
  return number.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: Number.isInteger(number) ? 0 : 2,
    maximumFractionDigits: 2,
  });
};

const formatTimestamp = (value) => {
  if (!value) return "Unknown time";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const actionSetForLane = (lane) => {
  if (lane === "waiting_on_them") return ["mark-handled", "reopen", "snooze", "void", "merge"];
  if (lane === "gated") return ["reopen", "reply-sent", "mark-handled", "void", "merge"];
  if (lane === "voided" || lane === "merged") return ["reopen"];
  if (lane === "handled") return ["reopen", "void", "merge"];
  return ["reply-sent", "mark-handled", "snooze", "void", "merge"];
};

export default function ObligationCard({
  obligation,
  mergeCandidates = [],
  ownerLabel,
  onAction,
  onOpenEvidence,
  onOpenProof,
  busyAction,
}) {
  const [snoozeDate, setSnoozeDate] = useState("");
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeFilter, setMergeFilter] = useState("");
  const [selectedMergeId, setSelectedMergeId] = useState("");
  const statusTone = obligation?.status?.tone || "attention";
  const StatusIcon = stateIcons[obligation?.status?.icon] || stateIcons[statusTone] || WarningCircle;
  const ChannelIcon = channelIcons[String(obligation?.provenance?.channel || "").toLowerCase()] || FileText;
  const actions = actionSetForLane(obligation?.lane);

  const resetMergePicker = () => {
    setMergeOpen(false);
    setMergeFilter("");
    setSelectedMergeId("");
  };

  const sortedMergeCandidates = [...mergeCandidates].sort((a, b) =>
    String(a?.title || "").localeCompare(String(b?.title || ""), undefined, { sensitivity: "base" })
  );
  const filteredMergeCandidates = sortedMergeCandidates.filter((candidate) => {
    const haystack = `${candidate?.title || ""} ${candidate?.counterparty || ""}`.toLowerCase();
    return haystack.includes(mergeFilter.trim().toLowerCase());
  });
  const selectedMergeTarget = sortedMergeCandidates.find((candidate) => candidate.id === selectedMergeId) || null;

  const handleConfirmMerge = async () => {
    if (!selectedMergeTarget) return;
    const ok = await onAction(obligation, "merge", { into_id: selectedMergeTarget.id });
    if (ok) resetMergePicker();
  };

  const toggleMergePicker = () => {
    if (mergeOpen) {
      resetMergePicker();
      return;
    }
    setMergeOpen(true);
  };

  return (
    <article
      data-testid={`obligation-card-${obligation.id}`}
      className="rounded-[1rem] border border-[hsl(var(--border))] bg-card px-4 py-3.5"
    >
      <h3 className="font-serif text-[0.95rem] font-medium leading-6 text-[hsl(var(--foreground))]">{obligation.title}</h3>
      <p className="mt-1 text-[12.5px] text-[hsl(var(--muted-foreground))]">
        {[obligation.counterparty, obligation.asset].filter(Boolean).join(" — ") || "Unassigned context"}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11.5px]">
        <span
          className={`inline-flex items-center rounded-full border px-2.5 py-1 ${ownerStyles[obligation.ownerParty] || ownerStyles.joint}`}
        >
          {obligation.ownerLabel || ownerLabel(obligation.ownerParty)}
        </span>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium ${statusStyles[statusTone] || statusStyles.attention}`}>
          <StatusIcon size={12} weight="fill" />
          {obligation.status?.label}
        </span>
        {obligation.amount !== undefined && obligation.amount !== null && obligation.amount !== "" ? (
          <span className="font-mono tabular-nums text-[hsl(var(--foreground))]">{formatMoney(obligation.amount)}</span>
        ) : null}
        {obligation.dueDate ? (
          <span className="ml-auto inline-flex items-center gap-1.5 text-[hsl(var(--muted-foreground))]">
            <CalendarDots size={12} />
            {formatShortDate(obligation.dueDate)}
          </span>
        ) : obligation.gateDate ? (
          <span className="ml-auto inline-flex items-center gap-1.5 text-[hsl(var(--muted-foreground))]">
            <CalendarDots size={12} />
            Returns {formatShortDate(obligation.gateDate)}
          </span>
        ) : null}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {actions.includes("reply-sent") ? (
          <Button
            type="button"
            size="sm"
            className="h-8 rounded-[0.625rem] px-3 text-[12px]"
            disabled={busyAction === "reply-sent"}
            onClick={() => onAction(obligation, "reply-sent")}
          >
            {busyAction === "reply-sent" ? "Working…" : obligationActionLabel("reply-sent")}
          </Button>
        ) : null}
        {actions.includes("mark-handled") ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 rounded-[0.625rem] border-[hsl(var(--input))] px-3 text-[12px]"
            disabled={busyAction === "mark-handled"}
            onClick={() => onAction(obligation, "mark-handled")}
          >
            {obligationActionLabel("mark-handled")}
          </Button>
        ) : null}
        {actions.includes("reopen") ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 rounded-[0.625rem] border-[hsl(var(--input))] px-3 text-[12px]"
            disabled={busyAction === "reopen"}
            onClick={() => onAction(obligation, "reopen")}
          >
            {obligationActionLabel("reopen")}
          </Button>
        ) : null}
      </div>

      {actions.includes("snooze") ? (
        <div className="mt-3 flex items-center gap-2">
          <Input
            type="date"
            value={snoozeDate}
            onChange={(event) => setSnoozeDate(event.target.value)}
            className="h-8 rounded-[0.625rem] border-[hsl(var(--input))] bg-white text-xs"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 rounded-[0.625rem] border-[hsl(var(--input))] px-3 text-[12px]"
            disabled={!snoozeDate || busyAction === "snooze"}
            onClick={() => onAction(obligation, "snooze", { gate_date: snoozeDate })}
          >
            {obligationActionLabel("snooze")}
          </Button>
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {actions.includes("void") ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 rounded-[0.625rem] px-2 text-[11.5px] text-[hsl(var(--muted-foreground))]"
            disabled={busyAction === "void"}
            onClick={() => onAction(obligation, "void")}
          >
            {obligationActionLabel("void")}
          </Button>
        ) : null}
        {actions.includes("merge") ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 rounded-[0.625rem] px-2 text-[11.5px] text-[hsl(var(--muted-foreground))]"
            disabled={busyAction === "merge"}
            onClick={toggleMergePicker}
          >
            {obligationActionLabel("merge")}
          </Button>
        ) : null}
      </div>

      {actions.includes("merge") && mergeOpen ? (
        <div className="mt-3 rounded-[0.875rem] border border-[hsl(var(--border))] bg-[hsl(var(--secondary))]/45 p-3">
          <Input
            type="text"
            value={mergeFilter}
            onChange={(event) => setMergeFilter(event.target.value)}
            placeholder="Filter merge targets"
            data-testid="merge-filter"
            className="h-8 rounded-[0.625rem] border-[hsl(var(--input))] bg-white text-xs"
          />

          <div className="mt-2 max-h-40 space-y-2 overflow-y-auto">
            {filteredMergeCandidates.length ? (
              filteredMergeCandidates.map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  data-testid="merge-target-row"
                  className={`flex w-full items-center gap-2 rounded-[0.75rem] border px-3 py-2 text-left transition-colors ${
                    selectedMergeId === candidate.id
                      ? "border-[hsl(var(--accent))] bg-white"
                      : "border-[hsl(var(--border))] bg-white/80 hover:border-[hsl(var(--input))]"
                  }`}
                  onClick={() => setSelectedMergeId(candidate.id)}
                >
                  <span className="min-w-0 flex-1 truncate text-[12.5px] text-[hsl(var(--foreground))]">{candidate.title}</span>
                  <span className="shrink-0 rounded-full bg-[#ecebe6] px-2.5 py-1 text-[10.5px] uppercase tracking-[0.08em] text-[#6b7384]">
                    {lanePillLabel[candidate.lane] || candidate.status?.label || candidate.lane}
                  </span>
                </button>
              ))
            ) : (
              <div className="rounded-[0.75rem] border border-dashed border-[hsl(var(--border))] px-3 py-3 text-[11.5px] text-[hsl(var(--muted-foreground))]">
                No merge targets match that filter.
              </div>
            )}
          </div>

          <div className="mt-3 flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="merge-confirm"
              className="h-8 rounded-[0.625rem] border-[hsl(var(--input))] px-3 text-[12px]"
              disabled={!selectedMergeTarget || busyAction === "merge"}
              onClick={handleConfirmMerge}
            >
              {busyAction === "merge" ? "Working…" : "Confirm merge"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              data-testid="merge-cancel"
              className="h-8 rounded-[0.625rem] px-3 text-[12px] text-[hsl(var(--muted-foreground))]"
              onClick={resetMergePicker}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      <div className="mt-3 flex items-center gap-2 text-[11.5px] text-[hsl(var(--muted-foreground))]">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-[hsl(var(--foreground))] underline decoration-[hsl(var(--accent))] underline-offset-4"
          onClick={() => onOpenProof(obligation)}
        >
          <Eye size={13} />
          How I know
        </button>
        {obligation.auditLabel ? (
          <span className="rounded-full border border-[hsl(var(--input))] px-2 py-0.5 text-[10.5px] uppercase tracking-[0.08em]">
            {obligation.auditLabel}
          </span>
        ) : null}
      </div>

      <div className="mt-3 flex items-center gap-2 border-t border-[hsl(var(--border))] pt-3 text-[11.5px] text-[hsl(var(--muted-foreground))]">
        <ChannelIcon size={13} className="text-[hsl(var(--accent))]" />
        <span className="truncate">
          {obligation.provenance?.origin || "Source"} · {formatTimestamp(obligation.provenance?.timestamp)}
        </span>
        <button
          type="button"
          className="ml-auto shrink-0 font-medium text-[hsl(var(--foreground))] underline decoration-[hsl(var(--accent))] underline-offset-4"
          onClick={() => onOpenEvidence(obligation)}
        >
          view
        </button>
      </div>
    </article>
  );
}
